import React, {useEffect, useState} from "react";
import _ from "lodash";
import {ChevronDownIcon, ChevronRightIcon, TagIcon} from "@heroicons/react/24/solid";
import ShowProgressSpinner from "../../../common/ui/show-progress-spinner";
import DeleteItem from "../../../common/ui/delete-item";
import SelectHtmlTag from "../../../content/components/rich-text-editor/select-html-tag";
import GenerateContentFromPrompt from "../../../openai/components/generate-content-from-prompt";
import {PRODUCT_BLOG_POST_PROMPTS} from "../../constants/product-blog-post.constant";
import AddEditProductImage from "../shopify-blog/add-edit-product-image";
import DeletePostContentLine from "../product-blog-post/delete-post-content-line";
import {
    addImagesToContentLines, prepareContentStatistics, prepareHtmlContentDetails,
    processContentLines,
    removeSectionHeader
} from "../../../content/utils/blog-content-util";
import {
    prepareExpandSectionContentPromptText, prepareExtractKeyWordsAndMapLinksPromptText,
    prepareKeywordLinkMapping, prepareRewriteSectionContentPromptText,
    prepareRewriteSectionTitlePromptText
} from "../../utils/product-blog-post-util";
import {SAVE_BLOG_POST_TIME_DELAY} from "../../../content/constants/blog.constant";
import {prepareGenericJSONPromptMessages} from "../../../design/utils/open-ai-prompt-util";
import {generateContent} from "../../../common/services/open-ai-service";
import Promise from "bluebird";
import {updateSingleBlogPostContent} from "../../../content/services/blog-post-content-data-service";


const DesignBlogPostEditMode = ({
                                    // inputContentLines,
                                    // htmlContent = '',
                                    productsList,
                                    selectedProductImageList = [],
                                    previewMode = false,
                                    blogPostContent,
                                    setBlogPostContent,
                                    saveInProgress,
                                    setSaveInProgress,
                                }) => {
    const [editDetail, setEditDetail] = useState(null);
    const [sectionTitlesMap, setSectionTitlesMap] = useState({});
    const [apiCallInProgressFor, setApiCallInProgressFor] = useState(null);
    const [mainContentPrompt, setMainContentPrompt] = useState(null);
    const [mainContent, setMainContent] = useState(null);
    const [contentLines, setContentLines] = useState(blogPostContent?.contentLines || []);
    const [htmlContent, setHtmlContent] = useState(blogPostContent?.htmlContent || '');
    let [changesCount, setChangesCount] = useState(0);
    const [addLinksInProgress, setAddLinksInProgress] = useState(false);
    const [currentContentLineIndex, setCurrentContentLineIndex] = useState(null);
    const [productLinkFields, setProductLinkFields] = useState([]);
    const [productUrls, setProductUrls] = useState(null);
    const [statistics, setStatistics] = useState(null);

    useEffect(() => {
        setContentLines(blogPostContent?.contentLines || []);
        setHtmlContent(blogPostContent?.htmlContent || '');

        openAllSections();
    }, [blogPostContent?.id]);

    const openAllSections = () => {
        const blogPostContentLines = blogPostContent?.contentLines || [];

        const sectionContentLines = _.filter(blogPostContentLines, (line) => line.isSection);
        const sectionTitles = _.map(sectionContentLines, 'text');

        const tempSectionTitlesMap = {};
        _.forEach(sectionTitles, (key) => {
            tempSectionTitlesMap[key] = true;
        });

        setSectionTitlesMap(tempSectionTitlesMap);
    }

    const handleShowHideSectionContent = (contentLine) => {
        const existingSectionTitlesMap = {...sectionTitlesMap};
        existingSectionTitlesMap[contentLine.text] = !existingSectionTitlesMap[contentLine.text];
        setSectionTitlesMap(existingSectionTitlesMap);
    }

    const handleEditDetailChanges = (event) => {
        const {name, value, type} = event.target;
        const existingEditDetail = {...editDetail};
        existingEditDetail[name] = value;
        setEditDetail(existingEditDetail);
    }

    const confirmEditDetailInfo = () => {
        const existingEditDetail = {...editDetail};
        const existingContentLines = [...contentLines];
        const existingContentLine = existingContentLines[existingEditDetail.contentLineIndex];

        if (existingContentLine.isSection) {
            prepareSectionTitles(existingEditDetail.text, existingEditDetail.contentLineIndex);
        } else {
            existingContentLine.text = existingEditDetail.text;
            existingContentLines[existingEditDetail.contentLineIndex] = existingContentLine;
            setContentLines(existingContentLines);
            incrementChangesCount();
        }

        cancelEditDetailInfo();
    }

    const cancelEditDetailInfo = () => {
        setEditDetail({isOpen: false, text: '', contentLineIndex: null});
    }

    const handleDeleteLink = (linkIndex, contentLineIndex) => {
        const existingContentLines = [...contentLines];
        existingContentLines[contentLineIndex].links.splice(linkIndex, 1);
        setContentLines(existingContentLines);
        incrementChangesCount();
    }

    const changeContentLineType = (contentLineIndex, newType) => {
        const existingContentLines = [...contentLines];
        const existingContentLine = existingContentLines[contentLineIndex];
        existingContentLine.newType = newType;

        if (newType === 'h2') {
            existingContentLine.text = removeSectionHeader(existingContentLine.text);
            const existingSectionTitlesMap = {...sectionTitlesMap};
            existingSectionTitlesMap[existingContentLine.text] = true;
            setSectionTitlesMap(existingSectionTitlesMap);
        }

        processContentLines(existingContentLines);
        setContentLines(existingContentLines);
        prepareStatistics(existingContentLines);
    }

    const openEditDetailInfo = (contentLine, contentLineIndex) => {
        const newEditDetail = {isOpen: true, text: contentLine.text, contentLineIndex: contentLineIndex};
        setEditDetail(newEditDetail);
    }

    const prepareExpandedMainContentLines = (generatedText) => {
        prepareContentLines(generatedText, true, false);
    }

    const prepareExpandedSectionContentLines = (generatedText, contentLineIndex) => {
        prepareContentLines(generatedText, false, true, contentLineIndex);
    }

    const prepareSectionTitles = (generatedText, contentLineIndex) => {
        let existingContentLines = [...contentLines];
        const existingContentLine = existingContentLines[contentLineIndex];

        const existingSectionTitlesMap = {...sectionTitlesMap};
        const previousSectionTitleInfo = existingSectionTitlesMap[existingContentLine.text];
        delete existingSectionTitlesMap[existingContentLine.text];

        const sectionContentLines = _.filter(existingContentLines, (line) => line.section === existingContentLine.text);
        let newSectionTitle = _.replace(_.trim(generatedText), /"/g, '');

        if (newSectionTitle?.indexOf('Section Title:') > -1) {
            newSectionTitle = _.split(newSectionTitle, 'Section Title:')[1];
        }

        existingContentLine.text = newSectionTitle;

        _.forEach(sectionContentLines, (contentLine) => {
            contentLine.section = newSectionTitle;
        });

        existingContentLines.splice(contentLineIndex, 1, existingContentLine);
        existingContentLines.splice(contentLineIndex + 1, sectionContentLines?.length, ...sectionContentLines);
        setContentLines(existingContentLines);

        existingSectionTitlesMap[newSectionTitle] = previousSectionTitleInfo;
        setSectionTitlesMap(existingSectionTitlesMap);

        prepareStatistics(existingContentLines);
    }

    const prepareRewrittenContentLine = (generatedText, contentLineIndex) => {
        let existingContentLines = [...contentLines];
        const existingContentLine = existingContentLines[contentLineIndex];
        let newContentLines: any = [];

        _.forEach(_.split(generatedText, '\n'), (line) => {
            if (!_.isEmpty(line)) {
                newContentLines.push({
                    text: _.trim(line),
                    newType: 'p',
                    section: existingContentLine.section
                });
            }
        });

        if (_.isEmpty(newContentLines)) {
            existingContentLines[contentLineIndex].text = _.trim(generatedText);
        } else {
            existingContentLines.splice(contentLineIndex, newContentLines?.length, ...newContentLines);
        }

        setContentLines(existingContentLines);
        prepareStatistics(existingContentLines);
    }

    const prepareExpandSectionContentPrompt = (contentLine) => {
        const existingContentLines = [...contentLines];
        const sectionContentLines = _.filter(existingContentLines, (line) => line.section === contentLine.text);
        let sectionContentText = contentLine.text + '\n';
        sectionContentText += _.join(_.map(sectionContentLines, 'text'), '\n');

        return prepareExpandSectionContentPromptText(sectionContentText);
    }

    const triggerContentLinkMappingProcess = async (sectionHeaderContentLine?) => {
        setAddLinksInProgress(true);
        let existingContentLines = [...contentLines];
        let promptContent, promptTitle;

        await Promise.each(existingContentLines, async (contentLine, contentLineIndex) => {
            setCurrentContentLineIndex(contentLineIndex);
            if (!sectionHeaderContentLine || (sectionHeaderContentLine && contentLine.section === sectionHeaderContentLine.text)) {
                if (!contentLine.isSection && !contentLine.isImage) {
                    promptContent = prepareExtractKeyWordsAndMapLinksPrompt(contentLine);
                    promptTitle = PRODUCT_BLOG_POST_PROMPTS.EXTRACT_KEYWORDS_AND_MAP_LINKS.title;
                    const generatedText = await initiateChatApiCall(promptTitle, promptContent, contentLine, contentLineIndex);

                    existingContentLines[contentLineIndex].links = prepareKeywordLinkMapping(generatedText, productLinkFields, contentLine?.text);
                    setContentLines(existingContentLines);
                    setApiCallInProgressFor(null);
                }
            }

            if (contentLine.isSection) {
                incrementChangesCount();
            }

        });

        incrementChangesCount();
        setAddLinksInProgress(false);
    }

    const prepareRewriteSectionTitlePrompt = (contentLine) => {
        const existingContentLines = [...contentLines];
        const sectionContentLines = _.filter(existingContentLines, (line) => line.section === contentLine.text);
        let sectionContentText = _.join(_.map(sectionContentLines, 'text'), '\n');

        return prepareRewriteSectionTitlePromptText(contentLine.text, sectionContentText);
    }

    const prepareRewriteSectionContentPrompt = (contentLine) => {
        return prepareRewriteSectionContentPromptText(contentLine.text);
    }

    const prepareExtractKeyWordsAndMapLinksPrompt = (contentLine) => {
        return prepareExtractKeyWordsAndMapLinksPromptText(contentLine.text, productUrls);
    }

    const addImageToContentLine = (productImage, contentLineIndex) => {
        const imageLine = {
            key: "P",
            type: "p",
            text: productImage.productTitle,
            html: `<a href="${productImage?.onlineStoreUrl}" target="_blank"><img src="${productImage?.url}" alt="${productImage.productTitle}"></a>`,
            newType: "br",
            isImage: true,
            productImageId: productImage.id,
            url: productImage?.url
        }

        const existingContentLines = [...contentLines];
        existingContentLines.splice(contentLineIndex + 1, 0, imageLine);

        processContentLines(existingContentLines);
        setContentLines(existingContentLines);
        incrementChangesCount();
    }

    const addMultipleImagesToContent = () => {
        const existingImages = [...selectedProductImageList];
        const existingContentLines = [...contentLines];

        const newContentLines = addImagesToContentLines(existingImages, existingContentLines);

        processContentLines(newContentLines);
        setContentLines(newContentLines);
        prepareStatistics(newContentLines);
    }

    const prepareContentLinks = (generatedText, contentLineIndex) => {
        const existingContentLines = [...contentLines];
        const mappingLinks = prepareKeywordLinkMapping(generatedText, productLinkFields, existingContentLines[contentLineIndex]?.text);
        addLinksToContentLine(mappingLinks, contentLineIndex);
    }

    const handleRemoveContentLine = (contentLineIndex) => {
        const existingContentLines = [...contentLines];
        existingContentLines.splice(contentLineIndex, 1);
        setContentLines(existingContentLines);
        prepareStatistics(existingContentLines);
    }

    const prepareStatistics = (existingContentLines) => {
        const generatedText = _.join(_.map(existingContentLines, 'text'), ' ');
        const newStatistics = prepareContentStatistics(existingContentLines, generatedText);
        const htmlContentText = prepareHtmlContentDetails(existingContentLines);

        setMainContent(generatedText);
        setStatistics(newStatistics);
        setHtmlContent(htmlContentText);

        incrementChangesCount();
    }

    const addLinksToContentLine = (links, contentLineIndex) => {
        const existingContentLines = [...contentLines];
        existingContentLines[contentLineIndex].links = links;

        processContentLines(existingContentLines);
        setContentLines(existingContentLines);
        prepareStatistics(existingContentLines);
    }

    const incrementChangesCount = () => {
        setChangesCount(++changesCount);
    }

    const resetChangesCount = () => {
        setChangesCount(0);
    }

    useEffect(() => {
        const timeoutId =
            setTimeout(async () => {
                if (changesCount > 0) {
                    resetChangesCount();
                    saveBlogPostContent();
                }
            }, SAVE_BLOG_POST_TIME_DELAY);

        return () => clearTimeout(timeoutId);
    }, [changesCount]);

    const prepareContentLines = (generatedText, isExpandMainContent?, isExpandSectionContent?, contentLineIndex?) => {
        let existingContentLines = [...contentLines];

        let newContentLines: any = [];

        _.forEach(_.split(generatedText, '\n'), (line) => {
            if (!_.isEmpty(line)) {
                if (!isExpandSectionContent || (isExpandSectionContent && line !== existingContentLines[contentLineIndex].text)) {
                    newContentLines.push({
                        text: _.trim(line),
                        newType: 'p',
                        section: isExpandSectionContent ? existingContentLines[contentLineIndex].text : null
                    });
                }
            }
        });

        if (isExpandSectionContent) {
            const sectionContentLines = _.filter(existingContentLines, (line) => line.section === existingContentLines[contentLineIndex].text);
            existingContentLines.splice(contentLineIndex + 1, sectionContentLines?.length, ...newContentLines);
        } else {
            existingContentLines = newContentLines;
        }

        setContentLines(existingContentLines);
        prepareStatistics(existingContentLines);
    }

    const initiateChatApiCall = async (promptTitle, promptContent, contentLine, contentLineIndex) => {
        setApiCallInProgressFor(contentLine?.text);
        const promptMessages = prepareGenericJSONPromptMessages(promptTitle, promptContent);
        let generatedContent: any = await generateContent(promptMessages);

        return generatedContent?.content;
    }

    const saveBlogPostContent = async () => {
        setSaveInProgress(true);
        const existingBlogPostContent: any = {...blogPostContent};
        existingBlogPostContent.contentLines = [...contentLines];
        existingBlogPostContent.htmlContent = htmlContent;

        existingBlogPostContent.statistics = {...statistics};
        setBlogPostContent(existingBlogPostContent);

        await updateSingleBlogPostContent(existingBlogPostContent);
        setSaveInProgress(false);
    }

    return (
        <>
            <div
                className={''}>
                {contentLines?.map((contentLine, contentLineIndex) => (
                    <div key={`contentLine-${contentLineIndex}`}
                         className={'flex justify-start'}>
                        {((contentLine.isSection || !contentLine.section) || (contentLine.section && sectionTitlesMap[contentLine.section])) &&

                            <>
                                <div
                                    className="flex justify-center self-center text-2xl text-white w-10">{contentLineIndex + 1}</div>

                                <div
                                    className={`w-full m-2 pl-4 pr-3 py-3 border border-gray-400 shadow-lg rounded-xl
                                                    ${_.isEmpty(contentLine.section) ? '' : 'ml-16'}
                                                    ${editDetail?.isOpen && editDetail?.contentLineIndex === contentLineIndex ? 'bg-sky-200' : 'bg-white'}`}>
                                    <div className={'flex items-center justify-between'}>
                                        {contentLine.isSection &&
                                            <div className={'mr-4'}>
                                                <button
                                                    className="button-pink-outline px-2 py-1 shadow-lg"
                                                    onClick={() => handleShowHideSectionContent(contentLine)}>
                                                    {sectionTitlesMap[contentLine.text] ?
                                                        <ChevronDownIcon className="h-12 w-6"/> :
                                                        <ChevronRightIcon className="h-12 w-6"/>
                                                    }
                                                </button>
                                            </div>
                                        }

                                        <div className={`w-full`}>
                                            <div
                                                className={`flex justify-between items-center
                                                                ${contentLine.newType === 'h3' ? 'font-bold text-sky-500' : ''}
                                                                ${contentLine.isSection ? 'font-bold text-pink-500' : ''}
                                                                ${contentLine.isImage ? 'font-bold text-purple-500' : ''}`}>

                                                {contentLine.isImage &&
                                                    <div
                                                        className={`flex justify-start font-semibold text-purple-500`}>
                                                        <TagIcon className="h-6 w-6 mr-2"/>
                                                        {contentLine?.text}
                                                    </div>
                                                }

                                                {!contentLine.isImage &&
                                                    (!editDetail?.isOpen || (editDetail?.isOpen && editDetail?.contentLineIndex !== contentLineIndex)) &&
                                                    <div>
                                                        {contentLine?.text}
                                                    </div>
                                                }

                                                {editDetail?.isOpen && editDetail?.contentLineIndex === contentLineIndex &&
                                                    <div
                                                        className={'w-full flex justify-between items-center'}>
                                                                        <textarea
                                                                            id="editDetailText"
                                                                            name="text"
                                                                            value={editDetail?.text}
                                                                            onChange={handleEditDetailChanges}
                                                                            className="w-full h-24 mr-2 border border-sky-500 p-4 rounded"/>
                                                        <div
                                                            className={'justify-center items-center'}>
                                                            <div>
                                                                <button
                                                                    onClick={confirmEditDetailInfo}
                                                                    className={`rounded flex items-center justify-center border button-outline-green m-1 w-20 px-2 py-1 text-sm`}>
                                                                    Confirm
                                                                </button>
                                                            </div>
                                                            <div>
                                                                <button
                                                                    onClick={cancelEditDetailInfo}
                                                                    className={`rounded flex items-center justify-center border button-gray-outline m-1 w-20 px-2 py-1 text-sm`}>
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }

                                                {apiCallInProgressFor === contentLine?.text &&
                                                    <div className={'ml-6'}>
                                                        <ShowProgressSpinner
                                                            sizeClass="w-6 h-6"
                                                            dataFetchInProgress={apiCallInProgressFor === contentLine?.text}/>
                                                    </div>
                                                }
                                            </div>

                                            {contentLine.links && contentLine.links.length > 0 &&
                                                <div className={'my-1'}>
                                                    {contentLine.links.map((link, linkIndex) => (
                                                        <div key={linkIndex}
                                                             className={'flex justify-start items-center mb-1'}>
                                                            <DeleteItem
                                                                handleDeleteItem={() => {
                                                                    handleDeleteLink(linkIndex, contentLineIndex);
                                                                }}
                                                                iconSize={'h-3 w-3'}
                                                                itemIndex={linkIndex}
                                                                showIcon={true}/>

                                                            <div className={'ml-1'}>
                                                                <a href={link.url}
                                                                   className={'ml-2 pr-2 underline text-blue-500'}>{link.url}</a>
                                                            </div>

                                                            <div
                                                                className={'ml-2 text-gray-500'}>{link.value}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            }

                                            {!previewMode &&
                                                <div
                                                    className={`flex justify-between items-center my-1`}>
                                                    <SelectHtmlTag
                                                        showKey={false}
                                                        contentLine={contentLine}
                                                        contentLineIndex={contentLineIndex}
                                                        changeContentLineType={changeContentLineType}/>

                                                    <div
                                                        className={'flex w-1/2 justify-end items-center'}>
                                                        {(!editDetail?.isOpen || (editDetail?.isOpen && editDetail?.contentLineIndex !== contentLineIndex)) &&
                                                            <div>
                                                                <button
                                                                    onClick={() => {
                                                                        openEditDetailInfo(contentLine, contentLineIndex);
                                                                    }}
                                                                    className={`rounded flex items-center justify-center border button-outline-green m-1 px-2 py-1 text-sm`}>
                                                                    Edit
                                                                </button>
                                                            </div>
                                                        }

                                                        {contentLine.isSection &&
                                                            <>
                                                                <GenerateContentFromPrompt
                                                                    label={'Expand'}
                                                                    noIcon={true}
                                                                    showCopyButton={false}
                                                                    sizeClass={'p-1 text-sm'}
                                                                    contentLineIndex={contentLineIndex}
                                                                    afterContentGenerated={prepareExpandedSectionContentLines}
                                                                    setApiCallInProgressFor={setApiCallInProgressFor}
                                                                    triggeredFor={contentLine?.text}
                                                                    promptTitle={PRODUCT_BLOG_POST_PROMPTS.EXPAND_CONTENT.title}
                                                                    promptContent={mainContentPrompt}
                                                                    previousResponse={mainContent}
                                                                    newPrompt={prepareExpandSectionContentPrompt(contentLine)}/>

                                                                <div>
                                                                    <button
                                                                        onClick={() => triggerContentLinkMappingProcess(contentLine)}
                                                                        className={`rounded flex items-center justify-center border button-outline-green m-1 px-2 py-1 text-sm`}>
                                                                        Add Links
                                                                    </button>
                                                                </div>

                                                                <GenerateContentFromPrompt
                                                                    label={'Rewrite Title'}
                                                                    noIcon={true}
                                                                    showCopyButton={false}
                                                                    sizeClass={'p-1 text-sm'}
                                                                    contentLineIndex={contentLineIndex}
                                                                    afterContentGenerated={prepareSectionTitles}
                                                                    setApiCallInProgressFor={setApiCallInProgressFor}
                                                                    triggeredFor={contentLine?.text}
                                                                    promptTitle={PRODUCT_BLOG_POST_PROMPTS.REWRITE_SECTION_TITLE.title}
                                                                    promptContent={prepareRewriteSectionTitlePrompt(contentLine)}/>
                                                            </>
                                                        }

                                                        {!contentLine.isSection && !contentLine.isImage &&
                                                            <>
                                                                {!contentLine?.isImage &&
                                                                    <div className={'m-1'}>

                                                                        <AddEditProductImage
                                                                            afterProductImageSelected={(event) => {
                                                                                addImageToContentLine(event, contentLineIndex);
                                                                            }}
                                                                            productsList={productsList}/>

                                                                    </div>
                                                                }

                                                                <GenerateContentFromPrompt
                                                                    label={'Add Links'}
                                                                    noIcon={true}
                                                                    showCopyButton={false}
                                                                    sizeClass={'p-1 text-sm'}
                                                                    contentLineIndex={contentLineIndex}
                                                                    afterContentGenerated={prepareContentLinks}
                                                                    setApiCallInProgressFor={setApiCallInProgressFor}
                                                                    triggeredFor={contentLine?.text}
                                                                    promptTitle={PRODUCT_BLOG_POST_PROMPTS.EXTRACT_KEYWORDS_AND_MAP_LINKS.title}
                                                                    promptContent={prepareExtractKeyWordsAndMapLinksPrompt(contentLine)}/>

                                                                <GenerateContentFromPrompt
                                                                    label={'Rewrite'}
                                                                    noIcon={true}
                                                                    showCopyButton={false}
                                                                    sizeClass={'p-1 text-sm'}
                                                                    contentLineIndex={contentLineIndex}
                                                                    afterContentGenerated={prepareRewrittenContentLine}
                                                                    setApiCallInProgressFor={setApiCallInProgressFor}
                                                                    triggeredFor={contentLine?.text}
                                                                    promptTitle={PRODUCT_BLOG_POST_PROMPTS.REWRITE_CONTENT.title}
                                                                    promptContent={prepareRewriteSectionContentPrompt(contentLine)}/>
                                                            </>
                                                        }

                                                        <div className={'ml-1'}>
                                                            <DeletePostContentLine
                                                                contentLineIndex={contentLineIndex}
                                                                handleRemoveContentLine={handleRemoveContentLine}
                                                                showIcon={true}/>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </>

                        }
                    </div>
                ))}
            </div>
        </>
    )
}

export default DesignBlogPostEditMode
